import { useMutation } from '@apollo/client';
import { FormProvider } from 'react-hook-form';
import { toast } from 'react-toastify';
import type { SoManagerGigaPanPartsFragment } from '~/apollo/generated/v4/graphql';
import { Button } from '~/components/ui/button';
import { HookFormErrors } from '~/components/ui/forms/HookFormErrors';
import { SOFormFields } from '~/components/upload/supporting-object/so-form-fields';
import type { SOParent } from '~/components/upload/supporting-object/so-manager';
import { UPDATE_GIGA_PAN } from '~/components/upload/supporting-object/so-operations';
import { useZodForm } from '~/utils/forms';
import {
  gigaPanSchema,
  initialGigaPan,
} from '~/utils/modules/supporting-object';

type Props = SOParent & {
  gigaPan: SoManagerGigaPanPartsFragment;
  onUpdateSuccess?: () => unknown;
};

export function GigaPanUpdateForm({
  gigaPan,
  studyId,
  onUpdateSuccess,
}: Props) {
  const [updateGigaPan, { loading, error }] = useMutation(UPDATE_GIGA_PAN);

  const form = useZodForm({
    schema: gigaPanSchema,
    values: initialGigaPan(gigaPan),
  });

  const handleSubmit = form.handleSubmit(async values => {
    try {
      await updateGigaPan({
        variables: {
          id: gigaPan.id,
          input: values,
        },
      });
      toast.success('Panorama updated successfully.');
      form.reset();
      if (onUpdateSuccess) onUpdateSuccess();
    } catch (err) {
      console.log('Error updating panorama', err);
      toast.error('There was a problem updating the panorama.');
    }
  });

  return (
    <FormProvider {...form}>
      <form onSubmit={handleSubmit} className="space-y-4">
        <SOFormFields soType="GIGA_PAN" studyId={studyId} />
        <HookFormErrors graphQLError={error} />

        <div className="card-actions justify-end">
          <Button type="submit" loading={loading} color="primary">
            Save
          </Button>
        </div>
      </form>
    </FormProvider>
  );
}
